/**
 * What she knows, one item at a time.
 *
 * Every material that tests something (a letter's sound, a word read, a sight
 * word found, a flash card) records an attempt. Attempts turn into a memory
 * per item, and the memory decides what comes back when: a word she knew
 * instantly returns in days, one she missed returns tomorrow. This is the
 * spaced repetition behind the CoffeePie method (Anki with a two year old:
 * about ten cards a deck a day, every deck every day, the parent grading),
 * written once here so the server, both apps and every material agree.
 *
 * The scheduler is FSRS-4.5 with its published default weights, the one
 * modern Anki uses, with intervals capped for a small child: at her age a
 * month away is long enough to forget anything.
 */

// ─────────────────────────────────────────────────────────────
// Items and attempts
// ─────────────────────────────────────────────────────────────

/** What an item is. The key is `<kind>:<id>`, e.g. `sound:sh`, `word:cat`, `card:hebrew/alef`. */
export type ItemKind = 'letter' | 'sound' | 'word' | 'sight' | 'fact' | 'card'

export const ITEM_KINDS: ItemKind[] = ['letter', 'sound', 'word', 'sight', 'fact', 'card']

export function itemKey(kind: ItemKind, id: string): string {
  return `${kind}:${id.trim().toLowerCase()}`
}

export function parseItemKey(key: string): { kind: ItemKind; id: string } | null {
  const i = key.indexOf(':')
  if (i < 1) return null
  const kind = key.slice(0, i) as ItemKind
  return ITEM_KINDS.includes(kind) ? { kind, id: key.slice(i + 1) } : null
}

/**
 * How the item was tested. Reading aloud to a parent is the method's core.
 * `exposure` is not a test: she met the item (a page of a book turned, a word
 * shown and then revealed) and nothing checked she could read it. It is kept
 * in her history but never counts toward what she knows, since a parent
 * reading aloud or a child paging through the pictures looks the same.
 * `parent` is a parent's own word on it ("she knows the cold"), see parentMark.
 */
export type AttemptMode = 'read' | 'choose' | 'listen' | 'blend' | 'build' | 'recall' | 'exposure' | 'parent'

/** One attempt at one item. */
export interface ItemEvent {
  item: string
  /** Right on this attempt. */
  ok: boolean
  /** When it happened (ms since epoch). */
  at: number
  /** How long she took, when the material could tell. */
  ms?: number
  /** She had help: a sound played for her, a hint, a second try. */
  help?: boolean
  /** A grade set directly (a parent's "easy"), overriding the one inferred. */
  grade?: Grade
  mode?: AttemptMode
  /** The material it came from. */
  source?: string
}

/** Anki's four answers. 1 again, 2 hard, 3 good, 4 easy. */
export type Grade = 1 | 2 | 3 | 4

/** Quick enough to count as instant, by how the item was tested. */
const FAST_MS: Record<AttemptMode, number> = { read: 2500, choose: 3000, listen: 3500, blend: 6000, build: 5000, recall: 3000, exposure: 2500, parent: 2500 }

/** An attempt that says something about what she knows: anything but an exposure. */
export function isEvidence(e: Pick<ItemEvent, 'mode' | 'ok'>): boolean {
  return e.mode !== 'exposure'
}

/** Turn an attempt into a grade. Wrong is again; right with help or slow is hard; right is good; right and instant is easy. */
export function gradeOf(e: ItemEvent): Grade {
  if (e.grade) return e.grade
  if (!e.ok) return 1
  if (e.help) return 2
  const fast = FAST_MS[e.mode ?? 'choose']
  if (typeof e.ms === 'number' && e.ms > 0) {
    if (e.ms <= fast) return 4
    if (e.ms > fast * 3) return 2
  }
  return 3
}

// ─────────────────────────────────────────────────────────────
// Memory (FSRS-4.5)
// ─────────────────────────────────────────────────────────────

/** One item's memory. Stability is in days: how long until recall falls to 90%. */
export interface ItemMemory {
  item: string
  stability: number
  difficulty: number
  /** When it should next come back (ms). */
  due: number
  last: number
  reps: number
  lapses: number
  /** Attempts seen and got right, for the parent's view. */
  seen: number
  right: number
  /** Right in a row, most recent first. */
  streak: number
  /** A running average of how long a right answer takes (ms), when known. */
  ms?: number
  /**
   * Sittings she got it right in, at least half a day apart. Two right
   * answers in one sitting show she followed along; right again on another
   * day shows she remembered. Rows from before this was kept count `right`.
   */
  rightDays?: number
  /** When the latest of those sittings began (ms). */
  rightDayAt?: number
}

/**
 * FSRS-4.5 default weights. The first four are the starting stability for a
 * first answer of each grade, tuned on adults; a toddler's first success says
 * far less (FIRST_STABILITY replaces them), and no single review may grow a
 * memory more than GROWTH_CAP times.
 */
const W = [0.4872, 1.4003, 3.7145, 13.8206, 5.1618, 1.2298, 0.8975, 0.031, 1.6474, 0.1367, 1.0461, 2.1072, 0.0793, 0.3246, 1.587, 0.2272, 2.8755]
const DECAY = -0.5
const FACTOR = 19 / 81
const DAY = 86_400_000
/** Days a first answer holds, by grade: again, hard, good, easy. */
const FIRST_STABILITY = [0.4, 0.8, 1.5, 3]
/** The most one review can multiply a memory's stability by. */
const GROWTH_CAP = 3
/** Attempts before an item can count as mastered, however quick they were. */
const MASTERY_REPS = 5
/** A right answer this long after the last counted sitting starts another: morning and evening are two. */
const SITTING_MS = 12 * 60 * 60 * 1000

/** How sure recall is to hold after this long: 0-1. */
export const RETENTION = 0.9
/** The longest gap for a small child, in days. */
export const MAX_INTERVAL_DAYS = 30

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x))

/** The chance she recalls it now (0-1). */
export function retrievability(m: Pick<ItemMemory, 'stability' | 'last'>, now: number): number {
  const days = Math.max(0, (now - m.last) / DAY)
  return Math.pow(1 + (FACTOR * days) / Math.max(m.stability, 0.01), DECAY)
}

function initDifficulty(g: Grade) {
  return clamp(W[4]! - (g - 3) * W[5]!, 1, 10)
}

function nextInterval(stability: number) {
  const days = (stability / FACTOR) * (Math.pow(RETENTION, 1 / DECAY) - 1)
  return clamp(Math.round(days), 1, MAX_INTERVAL_DAYS)
}

/**
 * Apply one attempt. A first attempt starts the memory; a miss drops it back
 * (it returns tomorrow and in today's session again); a hit grows it by how
 * hard the item is and how nearly it was forgotten.
 */
export function review(prev: ItemMemory | undefined, e: ItemEvent): ItemMemory {
  const g = gradeOf(e)
  const now = e.at
  const rightMs = e.ok && typeof e.ms === 'number' && e.ms > 0 ? e.ms : undefined
  const ms = rightMs === undefined ? prev?.ms : prev?.ms === undefined ? rightMs : Math.round(prev.ms * 0.7 + rightMs * 0.3)
  // A parent's word stands for what they have seen her do over time, so each of theirs is a sitting.
  const dayAt = prev?.rightDayAt ?? (prev?.right ? prev.last : undefined)
  const newDay = e.ok && (e.mode === 'parent' || dayAt === undefined || now - dayAt >= SITTING_MS)
  const days = { rightDays: (prev?.rightDays ?? prev?.right ?? 0) + (newDay ? 1 : 0), rightDayAt: newDay ? now : dayAt }
  if (!prev || prev.reps === 0) {
    const stability = FIRST_STABILITY[g - 1]!
    return {
      item: e.item, stability, difficulty: initDifficulty(g),
      due: now + (g === 1 ? 0 : nextInterval(stability) * DAY), last: now,
      reps: 1, lapses: g === 1 ? 1 : 0, seen: 1, right: e.ok ? 1 : 0, streak: e.ok ? 1 : 0, ms, ...days,
    }
  }
  // Two attempts on the same day do not grow a memory much: the spacing is what counts.
  const r = retrievability(prev, now)
  const d0 = prev.difficulty - W[6]! * (g - 3)
  const difficulty = clamp(W[7]! * initDifficulty(4) + (1 - W[7]!) * d0, 1, 10)
  let stability: number
  if (g === 1) {
    stability = W[11]! * Math.pow(prev.difficulty, -W[12]!) * (Math.pow(prev.stability + 1, W[13]!) - 1) * Math.exp(W[14]! * (1 - r))
    stability = Math.min(stability, prev.stability)
  } else {
    const hard = g === 2 ? W[15]! : 1
    const easy = g === 4 ? W[16]! : 1
    stability = prev.stability * (Math.exp(W[8]!) * (11 - prev.difficulty) * Math.pow(prev.stability, -W[9]!) * (Math.exp(W[10]! * (1 - r)) - 1) * hard * easy + 1)
    stability = Math.min(stability, prev.stability * GROWTH_CAP)
  }
  stability = clamp(stability, 0.1, 3650)
  return {
    item: e.item, stability, difficulty,
    due: now + (g === 1 ? 0 : nextInterval(stability) * DAY), last: now,
    reps: prev.reps + 1, lapses: prev.lapses + (g === 1 ? 1 : 0),
    seen: prev.seen + 1, right: prev.right + (e.ok ? 1 : 0), streak: e.ok ? prev.streak + 1 : 0, ms, ...days,
  }
}

/**
 * A parent's word on an item, as attempts for her history. The parent is
 * ground truth for what they have seen her do, so "she knows it" is two easy
 * reads, each its own sitting (enough to count as known), and "not
 * yet" is a miss, which brings it back soon. They go through review like any
 * attempt, so the games still check her and her memory moves on from there.
 */
export function parentMark(item: string, knows: boolean, at: number): ItemEvent[] {
  return knows
    ? [0, 1].map(() => ({ item, ok: true, at, grade: 4 as const, mode: 'parent' as const }))
    : [{ item, ok: false, at, mode: 'parent' as const }]
}

/** Replay a history of attempts, oldest first, into memories by item. */
export function replay(events: ItemEvent[], start: Record<string, ItemMemory> = {}): Record<string, ItemMemory> {
  const out = { ...start }
  for (const e of [...events].sort((a, b) => a.at - b.at)) if (isEvidence(e)) out[e.item] = review(out[e.item], e)
  return out
}

// ─────────────────────────────────────────────────────────────
// What she knows, for the parent
// ─────────────────────────────────────────────────────────────

export type Standing = 'new' | 'learning' | 'shaky' | 'known' | 'mastered'

/**
 * She has shown it: right in two sittings, and right the last time she
 * tried. Time away does not undo this, only a miss does, so what she has
 * reached (a level, a sight set, a book) stays reached while she is away and
 * the scheduler brings back what is fading.
 */
export function hasShown(m: ItemMemory | undefined): boolean {
  return !!m && m.reps > 0 && (m.rightDays ?? m.right) >= 2 && m.streak > 0
}

/**
 * Where an item stands. Mastered: it would hold for three weeks and she has
 * shown it at least five times. Known: she has shown it and it holds now.
 * Shaky: she has shown it but it is fading, and a quick review brings it
 * back. Learning: seen, not yet shown.
 */
export function standing(m: ItemMemory | undefined, now: number): Standing {
  if (!m || m.reps === 0) return 'new'
  const r = retrievability(m, now)
  if (m.stability >= 21 && r >= 0.85 && m.reps >= MASTERY_REPS) return 'mastered'
  // Known means remembered: right answers in one sitting are a start, not knowing.
  if (!hasShown(m)) return 'learning'
  if (r < 0.75) return 'shaky'
  return 'known'
}

/**
 * She has it, for everything a parent reads as reached: known, mastered, or
 * shaky (shown, and only fading with time). What opens levels, sets and books.
 */
export function holds(m: ItemMemory | undefined, now: number): boolean {
  const s = standing(m, now)
  return s !== 'new' && s !== 'learning'
}

/** Fresh in her memory now: known or mastered, not fading. What says a reached level has gone a little rusty. */
export function freshNow(m: ItemMemory | undefined, now: number): boolean {
  const s = standing(m, now)
  return s === 'known' || s === 'mastered'
}

/** She knows it on sight: right in two sittings, and not missed since. */
export function knowsOnSight(m: ItemMemory | undefined, now: number): boolean {
  return holds(m, now)
}

/**
 * Small sets learned in order (sight words, letter sounds): which are open.
 * The first always is; each after it opens once she knows half the set
 * before it, unless `opens` gives the set its own condition (setOpens). The
 * reading path shows the sets by this rule and her decks bring in new words
 * by it, so the two never teach on different schedules.
 */
export function openSets(sets: string[][], knows: (member: string) => boolean, opens?: Array<boolean | undefined>): boolean[] {
  return sets.map((_, i) => opens?.[i] ?? (i === 0 || sets[i - 1]!.filter(knows).length * 2 >= sets[i - 1]!.length))
}

/** A set's own condition: open once `need` of these memory items hold. */
export interface SetOpens { items: string[]; need: number }

/** Each set's own condition, met or not; undefined where a set follows the one before it. */
export function setOpens(opens: Array<SetOpens | null> | undefined, memories: Record<string, ItemMemory>, now: number): Array<boolean | undefined> | undefined {
  return opens?.map((o) => (o ? o.items.filter((k) => holds(memories[k], now)).length >= o.need : undefined))
}

/**
 * Cards that teach the members of ordered sets, so a deck brings in a new
 * one only once its set is open (waitingItems). Plain data, so the cards
 * engine gets the same gate the apps count by.
 */
export interface SetGate {
  /** The sets, in teaching order. */
  sets: string[][]
  /** Every memory item that shows she knows a member, by member. */
  evidence: Record<string, string[]>
  /** Each gated item (a card) and the member it teaches. */
  items: Record<string, string>
  /**
   * Sets that open on their own condition rather than the set before them,
   * by position (null follows the set before). The letter sounds open by the
   * blending level they serve, which is words she reads well.
   */
  opens?: Array<SetOpens | null>
}

/**
 * The gated items that wait: ones she has never met that teach a member of
 * a set not open yet. One she has met keeps coming back for review.
 */
export function waitingItems(gate: SetGate | SetGate[] | undefined, memories: Record<string, ItemMemory>, now: number): Set<string> {
  if (!gate) return new Set()
  if (Array.isArray(gate)) return new Set(gate.flatMap((g) => [...waitingItems(g, memories, now)]))
  const knows = (w: string) => (gate.evidence[w] ?? []).some((k) => knowsOnSight(memories[k], now))
  const open = openSets(gate.sets, knows, setOpens(gate.opens, memories, now))
  const shut = new Set(gate.sets.flatMap((set, i) => (open[i] ? [] : set)))
  return new Set(Object.entries(gate.items).filter(([k, w]) => shut.has(w) && !memories[k]?.reps).map(([k]) => k))
}

// ─────────────────────────────────────────────────────────────
// A session: what to show today
// ─────────────────────────────────────────────────────────────

export interface SessionPlanInput {
  /** Every item in order of the ladder: what comes first when it is new. */
  ladder: string[]
  memories: Record<string, ItemMemory>
  now: number
  /** How many cards in a session. The method found ten to be right. */
  size?: number
  /** Most new items in one session. The real limit is set by how she is doing. */
  maxNew?: number
  /** Her recent accuracy (0-1) on these items, if known; gates how many new ones come. */
  recentAccuracy?: number
  /** New items already introduced today (another session counts). */
  newToday?: number
  /** Items that come in as new only once their set is open (waitingItems). */
  gate?: SetGate | SetGate[]
  /**
   * Fill the session to `size` with practice on items she already knows
   * (default). A game with fixed rounds wants that; flash cards do not: a
   * card she knows that is not due is a card wasted, so they pass false and
   * a short day stays short.
   */
  fillWithKnown?: boolean
}

export interface SessionCard {
  item: string
  why: 'due' | 'new' | 'practice'
}

/**
 * Today's cards. Due items first, the most nearly forgotten first; then new
 * ones from the ladder, fewer when she has been missing; then practice on the
 * weakest items she already has, so a session is always about `size` long
 * (only those she does not hold fresh yet, with `fillWithKnown: false`).
 * New cards are spread between reviews, not bunched at the end.
 */
export function planSession(input: SessionPlanInput): SessionCard[] {
  const size = input.size ?? 10
  const { memories, now } = input
  const acc = input.recentAccuracy
  // Nothing seen yet: the deck's own limit (her first day has no accuracy to go on).
  // Otherwise her recent accuracy sets it.
  const fresh0 = input.ladder.every((k) => !memories[k] || memories[k]!.reps === 0)
  const gate = acc === undefined ? (fresh0 ? input.maxNew ?? 3 : 2) : acc >= 0.9 ? 3 : acc >= 0.75 ? 2 : acc >= 0.6 ? 1 : 0
  const newRoom = Math.max(0, Math.min(input.maxNew ?? 3, gate) - (input.newToday ?? 0))
  const seen = input.ladder.filter((k) => memories[k] && memories[k]!.reps > 0)
  // New cards get their room first, so a pile of reviews never stops her
  // meeting something new each day; reviews fill the rest.
  const waiting = waitingItems(input.gate, memories, now)
  const fresh = input.ladder.filter((k) => (!memories[k] || memories[k]!.reps === 0) && !waiting.has(k)).slice(0, Math.min(newRoom, size))
  const due = seen
    .filter((k) => memories[k]!.due <= now)
    .sort((a, b) => retrievability(memories[a]!, now) - retrievability(memories[b]!, now))
    .slice(0, size - fresh.length)
  const taken = new Set([...due, ...fresh])
  const practice = seen
    .filter((k) => !taken.has(k) && (input.fillWithKnown !== false || !freshNow(memories[k], now)))
    .sort((a, b) => retrievability(memories[a]!, now) - retrievability(memories[b]!, now))
    .slice(0, Math.max(0, size - due.length - fresh.length))
  const reviews: SessionCard[] = [...due.map((item) => ({ item, why: 'due' as const })), ...practice.map((item) => ({ item, why: 'practice' as const }))]
  // New cards go between reviews, evenly: a session opens on something she knows.
  const out: SessionCard[] = []
  let n = 0
  reviews.forEach((r, i) => {
    out.push(r)
    const slot = Math.round(((n + 1) * reviews.length) / (fresh.length + 1))
    if (n < fresh.length && i + 1 === slot) out.push({ item: fresh[n++]!, why: 'new' })
  })
  while (n < fresh.length) out.push({ item: fresh[n++]!, why: 'new' })
  return out.slice(0, size)
}

// ─────────────────────────────────────────────────────────────
// A day across every deck
// ─────────────────────────────────────────────────────────────

/**
 * About how many cards a two-year-old finishes in one sitting, roughly four
 * or five minutes. Every deck she has shares it, so a new deck never makes
 * her day longer, only fuller.
 */
export const DAY_CARDS = 12
/** A card takes her about this long, turn and talk included (ms). */
export const CARD_MS = 20_000

export interface DeckToday {
  /** The deck's own plan for today (planSession, plus its skills again). */
  plan: SessionCard[]
  /** Its cards she has ever met. */
  met: number
  /** Its cards she met for the first time today. */
  newToday: number
}

/**
 * Today's cards from every deck, inside one budget. Decks she is learning
 * come first and take turns, so each gets some: their due cards, then their
 * new ones, then practice. A deck she has never met rests until there is
 * room, and only one new deck starts a day (a deck whose every met card is
 * from today is that day's start), so a pile of fresh decks never buries the
 * ones she is in the middle of. Each deck keeps its own order. The cards
 * engine and both apps run this same function, so the count a parent reads
 * is the session she gets.
 */
export function shareDay(decks: DeckToday[], budget = DAY_CARDS): SessionCard[][] {
  const taken = decks.map(() => new Set<number>())
  let left = budget
  const round = (which: number[], ok: (c: SessionCard) => boolean) => {
    for (let moved = true; left > 0 && moved;) {
      moved = false
      for (const i of which) {
        if (left <= 0) break
        const j = decks[i]!.plan.findIndex((c, k) => !taken[i]!.has(k) && ok(c))
        if (j < 0) continue
        taken[i]!.add(j)
        left--
        moved = true
      }
    }
  }
  const all = decks.map((_, i) => i)
  const starting = all.filter((i) => decks[i]!.met > 0 && decks[i]!.met <= decks[i]!.newToday)
  const inPlay = all.filter((i) => decks[i]!.met > 0)
  round(inPlay, (c) => c.why === 'due')
  round(inPlay, (c) => c.why === 'new')
  const untouched = all.find((i) => decks[i]!.met === 0 && decks[i]!.plan.length)
  if (!starting.length && untouched !== undefined && left >= 3) round([untouched], () => true)
  round(inPlay, () => true)
  return decks.map((d, i) => d.plan.filter((_, k) => taken[i]!.has(k)))
}

/** Accuracy over attempts since `since` (0-1), or undefined when there are none. */
export function accuracySince(events: ItemEvent[], since: number): number | undefined {
  const recent = events.filter((e) => e.at >= since)
  return recent.length ? recent.filter((e) => e.ok).length / recent.length : undefined
}
